import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination
} from '@mui/material';
import { Add, Search, Edit, Delete, Close } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';

const WorkType = () => {
  const { user } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ typeOfWorkName: '', description: '' });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState({ typeOfWorkName: false, description: false });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dialogMessage, setDialogMessage] = useState({ text: '', type: '' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchAlert, setSearchAlert] = useState({ open: false, message: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);

  useEffect(() => {
    fetchWorkTypes();
  }, []);

  const fetchWorkTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.WORK_TYPES);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.type_work_id,
        typeOfWorkName: item.type_work_name,
        description: item.type_work_code
      }));
      setAllRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      setMessage({ text: 'Failed to load work types. Please check if the server is running.', type: 'error' });
      setAllRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const rowToEdit = filteredRows.find(row => row.id === id);
    setFormData({ typeOfWorkName: rowToEdit.typeOfWorkName, description: rowToEdit.description });
    setEditMode(true);
    setEditId(id);
    setMessage({ text: '', type: '' });
    setOpen(true);
  };

  const handleRowSelect = (id, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const visibleIds = new Set(paginatedRows.map(row => row.id));
      setSelectedRows(visibleIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleDeleteRow = (id) => {
    setDeleteRowId(id);
    setSingleDeleteConfirmOpen(true);
  };

  const handleSingleDeleteConfirm = async () => {
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(`${API_ENDPOINTS.WORK_TYPES}/${deleteRowId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete work type');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      await fetchWorkTypes();
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete work type', type: 'error' });
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchAlert({ open: true, message: 'Please enter search text' });
      return;
    }

    setSearchLoading(true);
    setTimeout(() => {
      const filtered = allRows.filter(row =>
        (row.typeOfWorkName && row.typeOfWorkName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.description && row.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredRows(filtered);
      setSearchLoading(false);
    }, 500);
  };

  const handleShowAll = () => {
    setFilteredRows(allRows);
    setSearchTerm('');
  };

  const handleSave = async () => {
    setErrors({ typeOfWorkName: false, description: false });
    setDialogMessage({ text: '', type: '' });

    const newErrors = {
      typeOfWorkName: !formData.typeOfWorkName.trim(),
      description: !formData.description.trim() || formData.description.trim().length !== 3
    };
    
    if (newErrors.typeOfWorkName || newErrors.description) {
      setErrors(newErrors);
      const errorMsg = !formData.typeOfWorkName.trim() && (!formData.description.trim() || formData.description.trim().length !== 3)
        ? 'Both Type Of Work Name and Type Of Work Code are mandatory. Type Of Work Code must be exactly 3 characters.'
        : !formData.typeOfWorkName.trim()
        ? 'Type Of Work Name is mandatory'
        : 'Type Of Work Code must be exactly 3 characters';
      setDialogMessage({ text: errorMsg, type: 'error' });
      setTimeout(() => {
        const firstErrorField = newErrors.typeOfWorkName ? 'typeOfWorkName' : 'description';
        document.querySelector(`input[name="${firstErrorField}"]`)?.focus();
      }, 100);
      return;
    }

    setSaveLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const url = editMode ? `${API_ENDPOINTS.WORK_TYPES}/${editId}` : API_ENDPOINTS.WORK_TYPES;
      const method = editMode ? 'PUT' : 'POST';
      
      console.log('API Call:', { method, url, editMode, editId });
            
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          typeOfWorkName: formData.typeOfWorkName.trim(),
          description: formData.description.trim(),
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} work type`);
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error(`Server error: ${response.status} - ${errorText}`);
        }
      }

      await fetchWorkTypes();
      setOpen(false);
      handleClear();
      setMessage({ text: `Work type ${editMode ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error saving work type:', error);
      setDialogMessage({ 
        text: error.message.includes('Work Type already exist') ? 'Work Type already exist' : error.message || `Failed to ${editMode ? 'update' : 'save'} work type`, 
        type: 'error' 
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({ typeOfWorkName: '', description: '' });
    setErrors({ typeOfWorkName: false, description: false });
    setDialogMessage({ text: '', type: '' });
    setEditMode(false);
    setEditId(null);
  };

  const handleDeleteClick = () => {
    if (!selectedRows || selectedRows.size === 0) {
      setMessage({ text: 'Please select items to delete', type: 'warning' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const selectedIds = Array.from(selectedRows);
    
    if (!selectedIds || selectedIds.length === 0) {
      setMessage({ text: 'No items selected for deletion', type: 'warning' });
      setDeleteConfirmOpen(false);
      return;
    }
    
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(API_ENDPOINTS.WORK_TYPES, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selectionModel: selectedIds,
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete work types');
      }

      const result = await response.json();
      setMessage({ text: result.message || 'Selected work types deleted successfully', type: 'success' });
      setSelectedRows(new Set());
      await fetchWorkTypes();
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete work types', type: 'error' });
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const paginatedRows = filteredRows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Work Type</Typography>

      {(loading || saveLoading || deleteLoading || searchLoading) && (
        <LinearProgress sx={{ mb: 2 }} />
      )}
      
      {searchAlert.open && (
        <Alert 
          severity="warning" 
          onClose={() => setSearchAlert({ open: false, message: '' })}
          sx={{ mb: 2 }}
        >
          {searchAlert.message}
        </Alert>
      )}
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button 
            variant="outlined" 
            startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />} 
            onClick={handleSearch}
            disabled={searchLoading}
          >
            Search
          </Button>
          {filteredRows.length !== allRows.length && (
            <Button variant="outlined" onClick={handleShowAll}>
              Show All
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />} 
            onClick={handleDeleteClick}
            disabled={!selectedRows || selectedRows.size === 0 || deleteLoading}
          >
            Delete ({selectedRows?.size || 0})
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditMode(false); setEditId(null); setOpen(true); }}>
            Add Work Type
          </Button>
        </Box>
      </Box>

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell padding="checkbox" sx={{ backgroundColor: '#0d47a1' }}>
                    <Checkbox
                      color="default"
                      indeterminate={selectedRows.size > 0 && selectedRows.size < paginatedRows.length}
                      checked={paginatedRows.length > 0 && paginatedRows.every(row => selectedRows.has(row.id))}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1' }}>
                    Type Of Work Name
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1' }}>
                    Type Of Work Code
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => {
                  const isItemSelected = selectedRows.has(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd !important' }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        sx={{ fontSize: '13px', padding: '8px 16px' }}
                      >
                        {row.typeOfWorkName}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                        {row.description}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Edit
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => handleEdit(row.id)}
                          />
                          <Delete
                            sx={{ cursor: 'pointer', color: 'error.main' }}
                            onClick={() => handleDeleteRow(row.id)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={(event, newPage) => setPaginationModel({ ...paginationModel, page: newPage })}
            onRowsPerPageChange={(event) => setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) })}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}
      
      {loading && (
        <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Work Type' : 'Add Work Type'}
          <Close sx={{ cursor: 'pointer' }} onClick={() => { setOpen(false); handleClear(); }} />
        </DialogTitle>
        <DialogContent>
          {dialogMessage.text && (
            <Alert severity={dialogMessage.type} sx={{ mb: 2 }}>
              {dialogMessage.text}
            </Alert>
          )}
          <TextField
            fullWidth
            name="typeOfWorkName"
            label="Type Of Work Name (Max 250 characters)"
            value={formData.typeOfWorkName}
            onChange={(e) => setFormData({ ...formData, typeOfWorkName: e.target.value })}
            error={errors.typeOfWorkName}
            helperText={errors.typeOfWorkName ? 'Type Of Work Name is required' : `${formData.typeOfWorkName.length}/250 characters`}
            sx={{ mb: 2, mt: 1 }}
            inputProps={{ maxLength: 250 }}
          />
          <TextField
            fullWidth
            name="description"
            label="Type Of Work Code (Exactly 3 characters)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value.toUpperCase() })}
            error={errors.description}
            helperText={errors.description ? 'Type Of Work Code must be exactly 3 characters' : `${formData.description.length}/3 characters`}
            inputProps={{ maxLength: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} disabled={saveLoading}>Clear</Button>
          <Button onClick={() => { setOpen(false); handleClear(); }} disabled={saveLoading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedRows?.size || 0} selected work type{(selectedRows?.size || 0) > 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={singleDeleteConfirmOpen} onClose={() => setSingleDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this work type?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleSingleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default WorkType;